import React from "react";

import { GroupWithChildren } from "@dashboard/shared/components/GroupFilterHeader/GroupFilterHeader";
import { Box, Collapse, IconButton, List, ListItem, ListItemButton, ListItemText, Typography } from "@mui/material";

import { useStyles } from "./GroupTree.style";

type Props = {
    group: GroupWithChildren;
    depth: number;
    selectedGroupId: number | null;
    expandedGroups: number[];
    onSelect: (groupId: number) => void;
    onToggleExpand: (groupId: number) => void;
};

export const GroupTreeItem = (props: Props) => {
    const { classes, cx } = useStyles();
    const hasChildren = props.group.children.length > 0;
    const isSelected = props.selectedGroupId === props.group.id;
    const isExpanded = props.expandedGroups.includes(props.group.id);

    const handleExpand: React.MouseEventHandler<HTMLSpanElement> = (e) => {
        e.stopPropagation();
        props.onToggleExpand(props.group.id);
    };

    const depthPadding = props.depth * 8;

    return (
        <React.Fragment>
            <ListItem style={{ paddingLeft: depthPadding }} disablePadding>
                <ListItemButton
                    selected={isSelected}
                    onClick={() => props.onSelect(props.group.id)}
                    className={classes.treeRowBg}
                    dense
                >
                    <Box
                        component="span"
                        className={cx(classes.expandIcon, hasChildren ? classes.expandIconVisible : classes.expandIconHidden)}
                        onClick={handleExpand}
                    >
                        <IconButton size="small" tabIndex={-1}>
                            <i
                                className={cx(
                                    "fa-solid",
                                    isExpanded ? "fa-chevron-down" : "fa-chevron-right",
                                    classes.chevronIcon,
                                )}
                            />
                        </IconButton>
                    </Box>
                    <i
                        className={cx(
                            "fa-regular",
                            isExpanded && hasChildren ? "fa-folder-open" : "fa-folder",
                            classes.folderIcon,
                        )}
                    />
                    <ListItemText
                        className={classes.listItemText}
                        primary={
                            <Typography className={classes.navText} noWrap>
                                {props.group.name}
                            </Typography>
                        }
                    />
                </ListItemButton>
            </ListItem>
            {hasChildren ? (
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        {props.group.children.map((child) => (
                            <GroupTreeItem
                                key={child.id}
                                group={child}
                                depth={props.depth + 1}
                                selectedGroupId={props.selectedGroupId}
                                expandedGroups={props.expandedGroups}
                                onSelect={props.onSelect}
                                onToggleExpand={props.onToggleExpand}
                            />
                        ))}
                    </List>
                </Collapse>
            ) : null}
        </React.Fragment>
    );
};
